/**
 * PAJ GPS-hundar (H1): typer och visningslogik för positioner som API:t hämtat från PAJ.
 * Webbläsaren frågar aldrig PAJ – bara /api/integrations/:markId/paj/*.
 */

export type PajFreshness = 'live' | 'stale' | 'lost';

export interface PajDogPosition {
  key: string;
  deviceId: number;
  dogId: string | null;
  name: string;
  deviceName: string | null;
  accountStatus: string;
  lat: number;
  lng: number;
  recordedAt: string;
  batteryPercent: number | null;
  speedKmh: number | null;
  source: string;
  reportIntervalSec: number | null;
  staleAfterSec: number;
  lostAfterSec: number;
  freshness: PajFreshness;
}

export interface PajDogTrack {
  key: string;
  name: string;
  minutes: number;
  points: [number, number, number][];
  reason: string | null;
}

/** Räknas om i klienten, så att markören grånar även mellan hämtningarna. */
export function pajFreshness(p: Pick<PajDogPosition, 'recordedAt' | 'staleAfterSec' | 'lostAfterSec'>, now = Date.now()): PajFreshness {
  const ageSec = (now - Date.parse(p.recordedAt)) / 1000;
  if (!Number.isFinite(ageSec)) return 'lost';
  if (ageSec >= p.lostAfterSec) return 'lost';
  return ageSec >= p.staleAfterSec ? 'stale' : 'live';
}

/** ”just nu”, ”för 4 min sedan”, ”för 2 h sedan”, ”för 1 dygn sedan”. */
export function agoLabel(iso: string, now = Date.now()): string {
  const sec = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (!Number.isFinite(sec)) return 'okänd tid';
  if (sec < 60) return 'just nu';
  const min = Math.floor(sec / 60);
  if (min < 60) return `för ${min} min sedan`;
  const h = Math.floor(min / 60);
  if (h < 24) return `för ${h} h sedan`;
  const d = Math.floor(h / 24);
  return `för ${d} dygn sedan`;
}

/** Kort statusrad under namnet: tom när positionen är färsk. */
export function freshnessLabel(p: Pick<PajDogPosition, 'recordedAt' | 'staleAfterSec' | 'lostAfterSec'>, now = Date.now()): string {
  const f = pajFreshness(p, now);
  if (f === 'live') return '';
  return f === 'lost' ? `tyst – senast ${agoLabel(p.recordedAt, now)}` : `senast ${agoLabel(p.recordedAt, now)}`;
}

export const PAJ_DOG_COLORS: Record<PajFreshness, string> = { live: '#EE8F09', stale: '#8d9188', lost: '#5d615a' };

const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/** Leaflet-markör (HTML) – orange som övriga hundar när färsk, grå när gammal, streckad när tyst. */
export function pajDogMarkerHtml(p: PajDogPosition, now = Date.now()): string {
  const f = pajFreshness(p, now);
  const status = freshnessLabel(p, now);
  return `<div style="position:relative;width:34px;height:34px" data-paj-freshness="${f}">
    <div style="width:34px;height:34px;border-radius:50%;background:${PAJ_DOG_COLORS[f]};border:3px ${f === 'lost' ? 'dashed' : 'solid'} #fff;
      display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5);font-size:17px;line-height:1;
      ${f === 'live' ? '' : 'filter:grayscale(0.6);opacity:0.85;'}">🐕</div>
    <div style="position:absolute;top:36px;left:50%;transform:translateX(-50%);white-space:nowrap;background:rgba(0,0,0,0.6);color:#fff;font:700 11px system-ui;padding:1px 5px;border-radius:6px;text-align:center">${esc(p.name)}${status ? `<div style="font:600 10px system-ui;color:#e6e6e6">${esc(status)}</div>` : ''}</div>
  </div>`;
}

export function pajDogPopupHtml(p: PajDogPosition, track: PajDogTrack | undefined, now = Date.now()): string {
  const f = pajFreshness(p, now);
  const time = new Date(p.recordedAt).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const rows = [
    `Position ${agoLabel(p.recordedAt, now)} (${time})${p.source === 'wifi' ? ' · wifi-position' : ''}`,
    p.batteryPercent != null ? `Batteri ${Math.round(p.batteryPercent)} %` : null,
    p.speedKmh != null ? `Hastighet ${Math.round(p.speedKmh)} km/h` : null,
    f === 'stale' ? 'Trackern har inte rapporterat på ett tag – positionen kan vara inaktuell.' : null,
    f === 'lost' ? 'Trackern rapporterar inte. Kontrollera batteri och täckning.' : null,
    p.accountStatus === 'reauth' ? 'PAJ-kontot måste anslutas igen under Profil → Integrationer.' : null,
    track ? (track.points.length > 1 ? `Spår: ${track.points.length} punkter senaste ${track.minutes >= 60 ? `${Math.round(track.minutes / 60)} h` : `${track.minutes} min`}` : track.reason ?? 'Inget spår') : null,
  ].filter(Boolean) as string[];
  return `<div style="font-family:sans-serif;min-width:180px">
    <div style="font-weight:700;font-size:14px;margin-bottom:4px">🐕 ${esc(p.name)}</div>
    ${rows.map(r => `<div style="font-size:12px;color:#555;margin-top:2px">${esc(r)}</div>`).join('')}
    <div style="font-size:11px;color:#888;margin-top:4px">PAJ GPS${p.deviceName && p.deviceName !== p.name ? ` · ${esc(p.deviceName)}` : ''}</div>
  </div>`;
}
